/**
 * SizeInput
 *
 * Two free-text size inputs with an optional unit toggle (in / cm / mm / ft).
 * - Order is HEIGHT × WIDTH by default (user-requested in Feb 2026 to match
 *   the way carpenters call out sizes).
 * - The size string is stored with the unit suffix (e.g. "84x36 in",
 *   "8x4 ft", "2400x1200 mm") so downstream code can convert to sqft
 *   accurately. `sizeToSqft` in `lib/store.js` understands these suffixes.
 *
 * Stays optional — leave both boxes blank to store an empty size.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

const UNIT_OPTIONS = [
  { value: 'in', label: 'in' },
  { value: 'ft', label: 'ft' },
  { value: 'cm', label: 'cm' },
  { value: 'mm', label: 'mm' },
];

// Strip "Hx W <unit>" → {h, w, unit}. Defaults unit to passed default.
function parseSize(combined, defaultUnit = 'in') {
  if (!combined) return { a: '', b: '', unit: defaultUnit };
  const s = String(combined).trim().toLowerCase();
  let unit = defaultUnit;
  let stripped = s;
  const mUnit = s.match(/\b(ft|feet|in|inch|inches|cm|mm)\b\s*$/);
  if (mUnit) {
    const u = mUnit[1];
    unit = (u === 'feet') ? 'ft' : (u === 'inch' || u === 'inches') ? 'in' : u;
    stripped = s.slice(0, -mUnit[0].length).trim();
  }
  const m = stripped.split(/\s*[x×]\s*/);
  return { a: (m[0] || '').replace(/['"]/g, ''), b: (m[1] || '').replace(/['"]/g, ''), unit };
}

function buildSize(a, b, unit) {
  const aa = String(a || '').trim();
  const bb = String(b || '').trim();
  if (!aa && !bb) return '';
  const core = aa && bb ? `${aa}x${bb}` : (aa || bb);
  return `${core} ${unit}`;
}

export function SizeInput({
  value,
  onChange,
  label = 'Size',
  helperText,
  testIdPrefix = 'size',
  order = 'HxW',           // 'HxW' (default, height first) or 'WxH'
  showUnitToggle = true,    // hide for dialogs that always store one unit
  defaultUnit = 'in',
}) {
  const parsed = useMemo(() => parseSize(value, defaultUnit), [value, defaultUnit]);
  // Local unit state so the user can toggle without retyping numbers.
  const [unit, setUnit] = useState(parsed.unit || defaultUnit);
  useEffect(() => {
    // Keep local unit in sync if the parent resets the value.
    if (parsed.unit && parsed.unit !== unit) setUnit(parsed.unit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const firstIsHeight = order === 'HxW';
  // `a` is the first input, `b` is the second.
  const a = parsed.a;
  const b = parsed.b;
  // Lift conditional template-literal test IDs OUT of JSX. The Emergent
  // visual-edits babel plugin crashes when it tries to trace a prop whose
  // value is a ternary inside a template literal (it null-derefs an AST
  // node from a cross-file lookup). Computing them up-front keeps the plugin
  // happy and the JSX cleaner.
  const firstTestId = `${testIdPrefix}-${firstIsHeight ? 'h' : 'w'}`;
  const secondTestId = `${testIdPrefix}-${firstIsHeight ? 'w' : 'h'}`;
  const unitTestId = `${testIdPrefix}-unit`;
  const firstPlaceholder = firstIsHeight ? 'H' : 'W';
  const secondPlaceholder = firstIsHeight ? 'W' : 'H';

  const emit = (na, nb, nu) => onChange(buildSize(na, nb, nu));

  const onUnit = (u) => {
    setUnit(u);
    emit(a, b, u);
  };

  return (
    <div>
      <Label className="text-sm font-medium">{label} <span className="text-xs text-muted-foreground">(optional)</span></Label>
      <div className="mt-1 flex items-center gap-1">
        <Input
          value={a}
          onChange={(e) => emit(e.target.value, b, unit)}
          placeholder={firstPlaceholder}
          className="flex-1 text-sm text-center"
          data-testid={firstTestId}
        />
        <span className="text-sm text-[#8F9485] px-1 select-none">×</span>
        <Input
          value={b}
          onChange={(e) => emit(a, e.target.value, unit)}
          placeholder={secondPlaceholder}
          className="flex-1 text-sm text-center"
          data-testid={secondTestId}
        />
        {showUnitToggle && (
          <select
            value={unit}
            onChange={(e) => onUnit(e.target.value)}
            className="h-9 text-xs border border-input rounded-md px-1 bg-background"
            data-testid={unitTestId}
          >
            {UNIT_OPTIONS.map(u => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        )}
      </div>
      {helperText && <p className="text-[10px] text-[#8F9485] mt-1">{helperText}</p>}
    </div>
  );
}

export default SizeInput;
